import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CatalogueApiService } from '@angular-workspace/services';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import {
  DATASET_REFERENCE_METRICS,
  DatasetPublicProfile,
  DatasetSampleImage,
  DEFAULT_DATASET_EVALUATION_METRICS,
  findHackathonDatasetBySlug,
  findHackathonDatasetForCatalogueItem,
  getDatasetPrimaryMetrics,
  getDatasetPublicProfile,
  getProblemStatementKind,
  hackathonDatasetToDetailView,
  HackathonDataset,
  HACKATHON_DATASETS,
  mapCatalogueItemToDatasetDetailView,
  ProblemStatementKind,
  TanuhDatasetDetailView,
} from '../hackathon-datasets.data';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import {
  SUBMIT_MODEL_ROUTE,
  submitModelQueryParams,
} from '../../submit-model/submit-model-navigation.util';
import {
  buildEvaluationMetricsFromItem,
  hasDatasetInputProfile,
  hasDatasetOutputProfile,
  hasDatasetStatisticsProfile,
  mergeDatasetPublicProfile,
  resolveMetricKeyLabel,
  TanuhEvaluationMetricDisplay,
} from '../../../shared/utils/tanuh-dataset-item-payload.util';
import { ReferenceDatasetDistributionChartComponent } from '../../../shared/components/reference-dataset-distribution-chart/reference-dataset-distribution-chart.component';

@Component({
  selector: 'app-reference-dataset-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    ReferenceDatasetDistributionChartComponent,
    ModalComponent,
  ],
  templateUrl: './reference-dataset-detail.component.html',
  styleUrl: './reference-dataset-detail.component.scss',
})
export class ReferenceDatasetDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly catalogueApiService = inject(CatalogueApiService);

  dataset: TanuhDatasetDetailView | null = null;
  publicProfile: DatasetPublicProfile | undefined;
  primaryMetrics: readonly string[] = [];
  secondaryMetricLabel = '';
  evaluationMetrics: TanuhEvaluationMetricDisplay[] = [];
  headerConfig: AssetHeaderConfig = { title: '', description: '' };
  isLoading = true;
  loadError: string | null = null;
  private catalogueItem?: Record<string, unknown>;
  showDensityModal = false;
  hoveredDensityClass: DatasetSampleImage['class'] | null = null;
  readonly densityGuideGridOrder: readonly DatasetSampleImage['class'][] = ['D', 'C', 'B', 'A'];
  readonly acrBiRadsUrl =
    'https://www.acr.org/Clinical-Resources/Reporting-and-Data-Systems/Bi-Rads';

  ngOnInit(): void {
    void this.loadDataset();
  }

  private async loadDataset(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id') ?? '';

    const staticMatch =
      findHackathonDatasetBySlug(id) ??
      HACKATHON_DATASETS.find((ds) => ds.id === id || ds.title === id);
    if (staticMatch) {
      this.applyDataset(hackathonDatasetToDetailView(staticMatch));
      this.isLoading = false;
      return;
    }

    try {
      const response = await this.catalogueApiService.getItemById(id);
      const item = this.extractItemFromResponse(response);
      if (!item) {
        this.loadError = 'Dataset not found.';
        this.isLoading = false;
        return;
      }

      this.applyDataset(mapCatalogueItemToDatasetDetailView(item), item);
    } catch {
      this.loadError = 'Unable to load dataset details.';
    } finally {
      this.isLoading = false;
    }
  }

  private extractItemFromResponse(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const res = response as Record<string, unknown>;
    if (res['result']) {
      const result = res['result'];
      if (Array.isArray(result)) {
        return (result[0] as Record<string, unknown>) ?? null;
      }
      return result as Record<string, unknown>;
    }
    if (res['data']) {
      const data = res['data'];
      if (Array.isArray(data)) {
        return (data[0] as Record<string, unknown>) ?? null;
      }
      return data as Record<string, unknown>;
    }
    if (Array.isArray(response)) {
      return (response[0] as Record<string, unknown>) ?? null;
    }

    return res;
  }

  private applyDataset(
    view: TanuhDatasetDetailView,
    item?: Record<string, unknown>,
  ): void {
    this.dataset = view;
    this.catalogueItem = item;
    this.publicProfile = mergeDatasetPublicProfile(
      getDatasetPublicProfile(view.leaderboardDatasetId),
      item,
    );
    this.primaryMetrics = getDatasetPrimaryMetrics(view).map((metric) =>
      resolveMetricKeyLabel(item, metric),
    );
    this.secondaryMetricLabel = resolveMetricKeyLabel(item, view.secondaryMetric ?? '');
    const apiMetrics = item ? buildEvaluationMetricsFromItem(item) : [];
    this.evaluationMetrics =
      apiMetrics.length > 0
        ? this.applyPrimaryMetricFlags(apiMetrics, view)
        : this.buildStaticEvaluationMetrics(view);

    this.headerConfig = {
      breadcrumbs: [
        { label: 'Home', route: '/' },
        { label: 'Benchmarking Datasets', route: '/reference' },
        { label: view.title },
      ],
      title: view.title,
      description: view.description,
    };
  }

  goBack(): void {
    void this.router.navigate(['/reference']);
  }

  get problemStatementKind(): ProblemStatementKind {
    return getProblemStatementKind(this.dataset?.problemStatement);
  }

  get hasBreastDensityGuide(): boolean {
    return (this.publicProfile?.sampleImages?.length ?? 0) > 0;
  }

  get densitySampleImages(): readonly DatasetSampleImage[] {
    return this.publicProfile?.sampleImages ?? [];
  }

  openDensityModal(): void {
    if (this.hasBreastDensityGuide) {
      this.showDensityModal = true;
    }
  }

  onDensityGuideHover(densityClass: DatasetSampleImage['class'] | null): void {
    this.hoveredDensityClass = densityClass;
  }

  getDensitySample(densityClass: DatasetSampleImage['class']): DatasetSampleImage | undefined {
    return this.densitySampleImages.find((sample) => sample.class === densityClass);
  }

  isDensityGuideHighlighted(densityClass: DatasetSampleImage['class']): boolean {
    return this.hoveredDensityClass === densityClass;
  }

  get hasDatasetStatistics(): boolean {
    return hasDatasetStatisticsProfile(this.publicProfile);
  }

  get hasInputProfile(): boolean {
    return hasDatasetInputProfile(this.publicProfile?.input);
  }

  get hasOutputProfile(): boolean {
    return hasDatasetOutputProfile(this.publicProfile?.output);
  }

  get hasInputOutputSections(): boolean {
    return this.hasInputProfile || this.hasOutputProfile;
  }

  submitModel(): void {
    const dataset = this.resolveHackathonDatasetForSubmit();
    const label = this.dataset?.title?.trim();
    const key =
      dataset?.leaderboardDatasetId ||
      dataset?.id ||
      this.dataset?.submitModelKey?.trim() ||
      this.dataset?.leaderboardDatasetId?.trim() ||
      this.dataset?.id?.trim();

    void this.router.navigate([SUBMIT_MODEL_ROUTE], {
      queryParams: submitModelQueryParams({
        datasetKey: key,
        label,
      }),
    });
  }

  private resolveHackathonDatasetFromKey(
    key: HackathonDataset['leaderboardDatasetId'],
  ): HackathonDataset | undefined {
    const trimmed = key.trim();
    return (
      HACKATHON_DATASETS.find(
        (item) => item.id === trimmed || item.leaderboardDatasetId === trimmed,
      ) ?? findHackathonDatasetBySlug(trimmed)
    );
  }

  private resolveHackathonDatasetForSubmit(): HackathonDataset | undefined {
    if (!this.dataset) {
      return undefined;
    }

    if (this.dataset.leaderboardDatasetId) {
      const bySlug = findHackathonDatasetBySlug(this.dataset.leaderboardDatasetId);
      if (bySlug) {
        return bySlug;
      }
    }

    const submitKey = this.dataset.submitModelKey?.trim();
    if (submitKey) {
      const byKey = HACKATHON_DATASETS.find(
        (item) => item.id === submitKey || item.leaderboardDatasetId === submitKey,
      );
      if (byKey) {
        return byKey;
      }
    }

    const byId = findHackathonDatasetBySlug(this.dataset.id);
    if (byId) {
      return byId;
    }

    const catalogueMatch = this.catalogueItem
      ? findHackathonDatasetForCatalogueItem({
          id: this.catalogueItem['id'] as string | undefined,
          label: this.catalogueItem['label'] as string | undefined,
          name: this.catalogueItem['name'] as string | undefined,
          title: this.catalogueItem['title'] as string | undefined,
        })
      : undefined;
    if (catalogueMatch) {
      return catalogueMatch;
    }

    return (
      findHackathonDatasetForCatalogueItem({
        id: this.dataset.id,
        title: this.dataset.title,
      }) ??
      HACKATHON_DATASETS.find(
        (item) =>
          item.title.toLowerCase() === this.dataset!.title.toLowerCase() ||
          item.id.toLowerCase() === this.dataset!.title.toLowerCase(),
      )
    );
  }

  formatEvaluationMetricValue(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
      return '';
    }
    return Number(value.toFixed(4)).toString();
  }

  hasEvaluationMetricValue(value: number | null | undefined): boolean {
    return value != null && Number.isFinite(value);
  }

  /** Expected value range for a metric, e.g. "0 – 1"; blank when no range was set at onboarding. */
  formatMetricRange(metric: TanuhEvaluationMetricDisplay): string {
    const hasMin = metric.min != null && Number.isFinite(metric.min);
    const hasMax = metric.max != null && Number.isFinite(metric.max);
    if (hasMin && hasMax) {
      return `${metric.min} – ${metric.max}`;
    }
    if (hasMin) {
      return `≥ ${metric.min}`;
    }
    if (hasMax) {
      return `≤ ${metric.max}`;
    }
    return '';
  }

  private buildStaticEvaluationMetrics(
    view: TanuhDatasetDetailView,
  ): TanuhEvaluationMetricDisplay[] {
    const labels = view.leaderboardDatasetId
      ? (DATASET_REFERENCE_METRICS[view.leaderboardDatasetId] ??
        DEFAULT_DATASET_EVALUATION_METRICS)
      : DEFAULT_DATASET_EVALUATION_METRICS;

    return labels.map((label) => ({
      key: label,
      label,
      value: Number.NaN,
      isPrimary: this.isPrimaryMetricLabel(label, view),
      isSecondary: this.isSecondaryMetricLabel(label, view),
    }));
  }

  private applyPrimaryMetricFlags(
    metrics: TanuhEvaluationMetricDisplay[],
    view: TanuhDatasetDetailView,
  ): TanuhEvaluationMetricDisplay[] {
    return metrics.map((metric) => ({
      ...metric,
      isPrimary: metric.isPrimary || this.isPrimaryMetricLabel(metric.label, view),
      isSecondary: metric.isSecondary || this.isSecondaryMetricLabel(metric.label, view),
    }));
  }

  private isPrimaryMetricLabel(label: string, view: TanuhDatasetDetailView): boolean {
    const labelLower = label.toLowerCase();
    return getDatasetPrimaryMetrics(view).some((primary) => {
      const primaryLower = primary.trim().toLowerCase();
      return (
        primaryLower.length > 0 &&
        primaryLower !== '—' &&
        (labelLower === primaryLower || labelLower.includes(primaryLower))
      );
    });
  }

  private isSecondaryMetricLabel(label: string, view: TanuhDatasetDetailView): boolean {
    const secondary = view.secondaryMetric?.trim().toLowerCase() ?? '';
    const labelLower = label.toLowerCase();
    return (
      secondary.length > 0 &&
      secondary !== '—' &&
      (labelLower === secondary || labelLower.includes(secondary))
    );
  }
}
